/**
 * Maps an {@link Engine} to its {@link QueryCompiler}.
 *
 * Compilers are wired in `register-default-compilers.ts`. Adding an engine means
 * registering a compiler, not changing the input contract.
 */
import { QueryBuilderError } from '../errors/query-builder-error';
import type { Engine } from '../input/input-types';
import type { QueryCompiler, TranspileResult } from './compiler';

export class CompilerRegistry {
    private readonly compilers = new Map<
        Engine,
        QueryCompiler<TranspileResult>
    >();

    register(engine: Engine, compiler: QueryCompiler<TranspileResult>): void {
        this.compilers.set(engine, compiler);
    }

    has(engine: Engine): boolean {
        return this.compilers.has(engine);
    }

    get(engine: Engine): QueryCompiler<TranspileResult> {
        const compiler = this.compilers.get(engine);
        if (!compiler) {
            const registered = [...this.compilers.keys()].join(', ') || 'none';
            throw new QueryBuilderError({
                code: 'UNSUPPORTED_ENGINE',
                path: '$.engine',
                message: `Unsupported engine: "${engine}". Registered engines: ${registered}.`,
                details: { engine },
            });
        }
        return compiler;
    }
}

/** The registry used by {@link QueryBuilder} by default. */
export const defaultRegistry = new CompilerRegistry();
